import React, { useEffect, useState } from "react";
import { AutoComplete, Button, DatePicker, Form, InputNumber, Radio, Select, Spin } from "antd";
import dayjs from "dayjs";

import Devices from "services/devices";
import { Device } from "types";
import { DEFAULT_DATETIME_FORMAT } from "utils/datetime";
import { RingLogsFilter } from "../../types";

const { Option } = Select;
const { RangePicker } = DatePicker;

const MEASUREMENT_KEY_OPTIONS = [
    { label: "Heart Rate", value: "heart_rate" },
    { label: "Stress", value: "stress" },
    { label: "Blood Oxygen", value: "blood_oxygen" },
    { label: "Heart Rate Variability", value: "heart_rate_variability" },
    { label: "Weight", value: "weight" },
];

interface RingLogsFiltersProps {
    onSubmit: (values: RingLogsFilter) => void;
    loading: boolean;
    filters?: RingLogsFilter;
}

const RingLogsFilters: React.FC<RingLogsFiltersProps> = ({ onSubmit, filters, loading }) => {
    const [devices, setDevices] = useState<Device[]>([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [form] = Form.useForm();

    const identifierType = Form.useWatch("identifierType", form);
    const logType = Form.useWatch("type", form);

    useEffect(() => {
        if (!filters) return;
        form.setFieldsValue({
            identifierType: filters.identifierType || "mac_address",
            macAddress: filters.macAddress,
            deviceId: filters.deviceId,
            type: filters.type || "all",
            measurementKey: filters.measurementKey,
            dateRange:
                filters.startDate && filters.endDate
                    ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                    : undefined,
        });
    }, [filters]);

    const fetchMacAddresses = async (value: any) => {
        if (!value || value.length < 3) {
            setDevices([]);
            return;
        }
        setLoadingDevices(true);
        try {
            const params = { filters: { mac_address: value } };
            const response = await Devices.getAllUsersDevices(params);
            const responseData = response.data.items || [];
            setDevices(responseData);
        } catch {
            // ignore
        }
        setLoadingDevices(false);
    };

    const handleFinish = (values: any) => {
        const startDate = values?.dateRange?.[0] || null;
        const endDate = values?.dateRange?.[1] || null;
        const result: RingLogsFilter = {
            identifierType: values.identifierType,
            macAddress: values.identifierType === "mac_address" ? values.macAddress : undefined,
            deviceId: values.identifierType === "device_id" ? values.deviceId : undefined,
            type: values.type || "all",
            measurementKey: values.type === "measurements" ? values.measurementKey : undefined,
            startDate: startDate ? dayjs(startDate).format(DEFAULT_DATETIME_FORMAT) : undefined,
            endDate: endDate ? dayjs(endDate).format(DEFAULT_DATETIME_FORMAT) : undefined,
        };
        onSubmit(result);
    };

    return (
        <Form
            layout="vertical"
            form={form}
            onFinish={handleFinish}
            initialValues={{ identifierType: "mac_address", type: "all" }}
        >
            <Form.Item name="identifierType" label="Identify device by">
                <Radio.Group>
                    <Radio.Button value="mac_address">MAC Address</Radio.Button>
                    <Radio.Button value="device_id">Device ID</Radio.Button>
                </Radio.Group>
            </Form.Item>

            {identifierType === "mac_address" && (
                <>
                    <Form.Item
                        name="macAddress"
                        label="MAC Address"
                        rules={[{ required: true, message: "Please enter a MAC address" }]}
                    >
                        <AutoComplete
                            options={devices.map((item) => ({
                                value: item.macAddress,
                                key: item.macAddress,
                            }))}
                            onSearch={fetchMacAddresses}
                        />
                    </Form.Item>
                    <Spin size="small" spinning={loadingDevices} />
                </>
            )}

            {identifierType === "device_id" && (
                <Form.Item
                    name="deviceId"
                    label="Device ID"
                    rules={[{ required: true, message: "Please enter a device ID" }]}
                >
                    <InputNumber style={{ width: "100%" }} min={1} />
                </Form.Item>
            )}

            <Form.Item name="type" label="Log Type">
                <Select>
                    <Option value="all">All</Option>
                    <Option value="steps">Steps</Option>
                    <Option value="sleep">Sleep</Option>
                    <Option value="measurements">Measurements</Option>
                </Select>
            </Form.Item>

            {logType === "measurements" && (
                <Form.Item name="measurementKey" label="Measurement Type">
                    <Select allowClear placeholder="All measurement types">
                        {MEASUREMENT_KEY_OPTIONS.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            <Form.Item name="dateRange" label="Date Range">
                <RangePicker showTime />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                    Filter
                </Button>
            </Form.Item>
        </Form>
    );
};

export default RingLogsFilters;
